import { useDispatch, useSelector } from "react-redux";
import { useEffect, useState } from "react";
import { setTitleBar } from "store/titleBar.slice";
import { useTranslation } from "react-i18next";
import Loader from "components/loader/loader";
import { useGetActivitiesByCollectionQuery, useGetCollectionsQuery } from "services/collections.service";
import type { sectionDataType } from "components/scrollableSection/scrollableSection";
import Collection from "components/collection/collection";
import "./library.scss"
import ScrollableSection from "components/scrollableSection/scrollableSection";

const Library = () => {

    const dispatch = useDispatch();
    const { t } = useTranslation();
    const { data: collectionsList, isLoading: isLoadingCollections } = useGetCollectionsQuery();
    const [scopeCollectionsIds, setScopeCollectionsIds] = useState<string[]>([]);
    const [isSearching, setIsSearching] = useState<boolean>(false);
    const [selectedCollectionId, setSelectedCollectionId] = useState<string | null>(null);
    const searchValue: string = useSelector((state: any) => state.titleBar.searchValue);
    const theme = useSelector((state: any) => state.theme);
    const token = sessionStorage.getItem('accessToken');

    const { data: result, isFetching: isFetchingResources } = useGetActivitiesByCollectionQuery(
        {
            scopeCollections: scopeCollectionsIds,
            limit: 50,
            keywords: searchValue,
            offset: 0
        },
        { skip: !isSearching || !!selectedCollectionId });

    useEffect(() => {
        if (!token) return;

        dispatch(setTitleBar({
            title: "title_library",
            data: collectionsList || [],
            filteredData: collectionsList || [],
            isBackButton: false,
            isSearchBar: true,
            overrideSearchFn: true,
        }));

        return () => {
            dispatch(setTitleBar({ title: "", data: [], filteredData: [], overrideSearchFn: false }));
        }
    }, []);

    useEffect(() => {
        if (!collectionsList) return;
        setScopeCollectionsIds(() => {
            const ids = Object.values(collectionsList).map(
                (section: sectionDataType) => section.sectionId
            );
            return ids;
        })
    }, [collectionsList]);

    useEffect(() => {
        setIsSearching(searchValue !== '' && searchValue !== undefined)
    }, [searchValue]);

    const handleToggleCollection = (id: string) => {
        setSelectedCollectionId(prev => (prev === id ? null : id));
    };

    const isLoading = isLoadingCollections || isFetchingResources;

    return (
        <div className="library">
            {isLoading ? (
                <Loader className={"loader_tertiary"} />
            ) : (
                <>
                    {/* Display all collections */}
                    {!selectedCollectionId && collectionsList && Object.keys(collectionsList).length > 0 && !isSearching && (
                        Object.entries(collectionsList as Record<string, sectionDataType>).map(
                            ([key, collection], idx) => (
                                <Collection
                                    key={key}
                                    idx={idx}
                                    section={collection}
                                    isMobile={theme.isTabletPortraitOrMobile}
                                    isSelected={false}
                                    onToggle={handleToggleCollection}
                                />
                            )
                        )
                    )}

                    {/* Display selected collection with his resources */}
                    {selectedCollectionId && collectionsList && (
                        <Collection
                            key={selectedCollectionId}
                            idx={0}
                            section={collectionsList[selectedCollectionId]}
                            isMobile={theme.isTabletPortraitOrMobile}
                            isSelected={true}
                            onToggle={handleToggleCollection}
                        />
                    )}

                    {/* Global search result */}
                    {!selectedCollectionId && isSearching && (
                        <ScrollableSection
                            id={"search_results"}
                            idx={0}
                            title={`${t("searchResult")} : ${result?.count ?? '-'}`}
                            isScrollable={false}
                            isSeeMore={true}
                            hideSeeMoreButton={true}
                            items={result?.resources}
                            isMobile={theme.isTabletPortraitOrMobile}
                            isLoading={isFetchingResources}
                        />
                    )}

                    {/* No collections to display */}
                    {(!collectionsList || Object.keys(collectionsList).length === 0) && (
                        <div className="noActivities centered">
                            {t("noCollection")}
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default Library;
